import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdDownload, MdInfoOutline } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';

// Financeiro → Royalties: the distributor's streaming royalties, one row per
// month, with the CSV downloads. Closed months are final and are the ones in
// the balance (Pagamentos); a month generated but not yet closed is shown as
// provisional and cannot be downloaded.

const fmtAoa = (n) => `${new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(Number(n) || 0)} Kz`;
const fmtUsd = (n) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);
const fmtCount = (n) => new Intl.NumberFormat('pt-PT').format(Number(n) || 0);
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const fmtMonth = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[(m || 1) - 1]} ${y}`; };
const fmtDate = (s) => {
    if (!s) return '—';
    const d = new Date(String(s).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
};
const card = 'rounded-xl border border-gray-200 bg-white';
const th = 'px-4 py-2.5 font-semibold';

async function download(params) {
    try {
        const res = await izenuAPI.get('/distributor/royalties/download.php', { params, responseType: 'blob' });
        const name = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '')?.[1] || 'royalties.csv';
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
    } catch (e) {
        // The error body comes back as a blob too.
        let message = 'Não foi possível descarregar.';
        try { message = JSON.parse(await e.response.data.text()).message || message; } catch { /* keep the generic one */ }
        window.alert(message);
    }
}

function DownloadButton({ children, params, primary }) {
    const [busy, setBusy] = useState(false);
    const run = async () => { setBusy(true); await download(params); setBusy(false); };
    return (
        <button type="button" onClick={run} disabled={busy}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60 ${primary ? 'bg-amber-500 !text-white hover:bg-amber-600' : 'border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
            <MdDownload size={15} /> {busy ? 'A descarregar…' : children}
        </button>
    );
}

/** One file per year (at most 12 months), or every closed month. */
function YearDownload({ years }) {
    const [year, setYear] = useState(years[0] || '');
    return (
        <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold text-gray-700">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
                <option value="">Todos os anos</option>
            </select>
            <DownloadButton primary params={year ? { kind: 'statement', year } : { kind: 'statement' }}>
                Descarregar {year || 'tudo'} (CSV)
            </DownloadButton>
        </div>
    );
}

export default function RoyaltyHistory() {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState('');

    useEffect(() => {
        izenuAPI.get('/distributor/royalties/list.php')
            .then(({ data }) => setData(data))
            .catch((e) => setFailed(e?.response?.data?.message || 'Não foi possível carregar os royalties.'));
    }, []);

    if (failed) return <div className="p-6"><p className="text-sm text-red-600">{failed}</p></div>;
    if (!data) return <div className="p-6"><p className="text-sm text-gray-500">A carregar…</p></div>;

    const allMonths = data.months || [];
    const closed = allMonths.filter((m) => m.status === 'closed');
    // Only the last 12 months on screen; the full history is in the CSV.
    const months = allMonths.slice(0, 12);
    const paidToUs = data.payout_to === 'distributor';
    const total = closed.reduce((s, m) => s + m.amount_aoa, 0);
    const years = [...new Set(closed.map((m) => m.period.slice(0, 4)))].sort().reverse();

    return (
        <div className="space-y-6 p-4 text-left sm:p-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Royalties de streaming</h1>
                    <p className="mt-1 text-sm text-gray-600">Só as reproduções dos artistas que geris, mês a mês, com os ficheiros para download. As vendas de álbuns estão em <Link to="/sales-report" className="font-semibold text-amber-700 hover:underline">Vendas de álbuns</Link>.</p>
                </div>
                {closed.length > 0 && <YearDownload years={years} />}
            </div>

            <div className={`${card} flex items-start gap-3 p-4 text-sm text-gray-600`}>
                <MdInfoOutline className="mt-0.5 shrink-0 text-amber-600" size={18} />
                <div className="space-y-1">
                    <p>
                        Os royalties são calculados uma vez por mês, depois de o mês terminar, e passam a definitivos quando a Izenu <b className="text-gray-900">fecha o mês</b>.
                        {paidToUs
                            ? <> Os meses fechados <b className="text-gray-900">já estão incluídos no saldo</b> da distribuidora, em <Link to="/payouts" className="font-semibold text-amber-700 hover:underline">Pagamentos</Link>: não são um valor a receber à parte.</>
                            : <> A Izenu paga estes valores <b className="text-gray-900">directamente aos artistas</b>; aqui ficam para consulta.</>}
                    </p>
                    <p>
                        <b className="text-gray-900">Extrato</b>: uma linha por artista (reproduções, pontos, USD e Kz).{' '}
                        <b className="text-gray-900">Detalhe por faixa</b>: o relatório do mês, faixa a faixa, com ISRC.
                    </p>
                </div>
            </div>

            {closed.length > 0 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className={`${card} p-4`}>
                        <p className="text-xs font-semibold uppercase text-gray-500">{paidToUs ? 'Royalties já no saldo' : 'Royalties dos artistas'}</p>
                        <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{fmtAoa(total)}</p>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {paidToUs ? <>Meses fechados, já somados em <Link to="/payouts" className="font-semibold text-amber-700 hover:underline">Pagamentos</Link></> : 'Meses fechados, pagos directamente aos artistas'}
                        </p>
                    </div>
                    <div className={`${card} p-4`}><p className="text-xs font-semibold uppercase text-gray-500">Meses fechados</p><p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{closed.length}</p></div>
                    <div className={`${card} p-4`}><p className="text-xs font-semibold uppercase text-gray-500">Reproduções pagas</p><p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{fmtCount(closed.reduce((s, m) => s + m.streams, 0))}</p></div>
                </div>
            )}

            {months.length === 0 ? (
                <p className={`${card} p-4 text-sm text-gray-500`}>Ainda não há meses com royalties. Os royalties são calculados uma vez por mês, depois de o mês terminar.</p>
            ) : (
                <div className={`${card} overflow-x-auto`}>
                    <table className="w-full min-w-[760px] text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase text-gray-500">
                                <th className={th}>Mês</th><th className={th}>Estado</th>
                                <th className={`${th} text-right`}>Artistas</th><th className={`${th} text-right`}>Reproduções pagas</th>
                                <th className={`${th} text-right`}>USD</th><th className={`${th} text-right`}>Royalties</th><th className={th} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {months.map((m) => (
                                <tr key={m.period} className="align-middle">
                                    <td className="whitespace-nowrap px-4 py-2.5 font-semibold capitalize text-gray-900">{fmtMonth(m.period)}</td>
                                    <td className="px-4 py-2.5">
                                        {m.status === 'closed'
                                            ? <span className="text-xs font-semibold text-green-700">Fechado · {fmtDate(m.closed_at)}</span>
                                            : <span className="text-xs font-semibold text-amber-700" title="Ainda pode mudar até a Izenu fechar o mês">Em apuramento · ainda não está no saldo</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{fmtCount(m.artists)}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{fmtCount(m.streams)}</td>
                                    <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-gray-600">{fmtUsd(m.amount_usd)}</td>
                                    <td className={`whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums ${m.status === 'closed' ? 'text-gray-900' : 'text-gray-500'}`}>{fmtAoa(m.amount_aoa)}</td>
                                    <td className="px-4 py-2.5">
                                        {m.status === 'closed' ? (
                                            <div className="flex justify-end gap-2">
                                                <DownloadButton params={{ kind: 'statement', period: m.period }}>Extrato</DownloadButton>
                                                {m.has_tracks
                                                    ? <DownloadButton params={{ kind: 'tracks', period: m.period }}>Detalhe por faixa</DownloadButton>
                                                    : <span className="self-center text-xs text-gray-400" title="O relatório por faixa deste mês não foi gerado para a distribuidora">Sem detalhe por faixa</span>}
                                            </div>
                                        ) : <p className="text-right text-xs text-gray-400">Disponível quando o mês fechar</p>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {allMonths.length > 12 && (
                <p className="text-xs text-gray-500">Mostramos os últimos 12 meses. Os anteriores estão nos ficheiros por ano (botão no topo).</p>
            )}
        </div>
    );
}
